import { useState, useEffect, useRef } from 'react';

export interface DebugLogEntry {
  timestamp: Date;
  type: 'info' | 'success' | 'warning' | 'error' | 'request' | 'response';
  source: 'AutoTask' | 'TimeTech' | 'Cosmos' | 'Calendar' | 'System';
  message: string;
  details?: string;
}

// Global debug log store
let debugLogs: DebugLogEntry[] = [];
let listeners: ((logs: DebugLogEntry[]) => void)[] = [];
let hasNewErrors = false;
let errorListeners: ((hasErrors: boolean) => void)[] = [];

/**
 * Add a debug log entry (call from anywhere)
 */
export function addDebugLog(entry: Omit<DebugLogEntry, 'timestamp'>) {
  const fullEntry: DebugLogEntry = {
    ...entry,
    timestamp: new Date(),
  };
  debugLogs = [...debugLogs, fullEntry].slice(-500); // Keep last 500 entries
  listeners.forEach(listener => listener(debugLogs));

  // Track if there are new errors
  if (entry.type === 'error' || entry.type === 'warning') {
    hasNewErrors = true;
    errorListeners.forEach(listener => listener(true));
  }
}

/**
 * Clear all debug logs
 */
export function clearDebugLogs() {
  debugLogs = [];
  hasNewErrors = false;
  listeners.forEach(listener => listener(debugLogs));
  errorListeners.forEach(listener => listener(false));
}

/**
 * Mark errors as seen
 */
export function markErrorsSeen() {
  hasNewErrors = false;
  errorListeners.forEach(listener => listener(false));
}

/**
 * Subscribe to debug log updates
 */
function useDebugLogs() {
  const [logs, setLogs] = useState<DebugLogEntry[]>(debugLogs);

  useEffect(() => {
    const listener = (newLogs: DebugLogEntry[]) => setLogs([...newLogs]);
    listeners.push(listener);
    setLogs([...debugLogs]); // Get initial state
    return () => {
      listeners = listeners.filter(l => l !== listener);
    };
  }, []);

  return logs;
}

/**
 * Subscribe to error indicator
 */
function useHasErrors() {
  const [hasErrors, setHasErrors] = useState(hasNewErrors);

  useEffect(() => {
    const listener = (errors: boolean) => setHasErrors(errors);
    errorListeners.push(listener);
    return () => {
      errorListeners = errorListeners.filter(l => l !== listener);
    };
  }, []);

  return hasErrors;
}

/**
 * Floating Debug Panel - always visible, even over modals
 */
export default function FloatingDebugPanel() {
  const [isOpen, setIsOpen] = useState(false);
  const logs = useDebugLogs();
  const hasErrors = useHasErrors();
  const logsEndRef = useRef<HTMLDivElement>(null);
  const [autoScroll, setAutoScroll] = useState(true);

  // Auto-scroll to bottom when new logs arrive
  useEffect(() => {
    if (isOpen && autoScroll && logsEndRef.current) {
      logsEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [logs, autoScroll, isOpen]);

  // Mark errors as seen when panel is opened
  useEffect(() => {
    if (isOpen && hasErrors) {
      markErrorsSeen();
    }
  }, [isOpen, hasErrors]);

  const getTypeColor = (type: DebugLogEntry['type']) => {
    switch (type) {
      case 'success': return 'text-green-400';
      case 'warning': return 'text-yellow-400';
      case 'error': return 'text-red-400';
      case 'request': return 'text-blue-400';
      case 'response': return 'text-purple-400';
      default: return 'text-slate-300';
    }
  };

  const getSourceColor = (source: DebugLogEntry['source']) => {
    switch (source) {
      case 'AutoTask': return 'bg-blue-600';
      case 'TimeTech': return 'bg-purple-600';
      case 'Cosmos': return 'bg-green-600';
      case 'Calendar': return 'bg-orange-600';
      default: return 'bg-slate-600';
    }
  };

  const formatTime = (date: Date) => {
    const timeStr = date.toLocaleTimeString('en-US', {
      hour12: false,
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
    const ms = date.getMilliseconds().toString().padStart(3, '0');
    return `${timeStr}.${ms}`;
  };

  const copyToClipboard = async () => {
    const text = logs.map(log =>
      `[${formatTime(log.timestamp)}] [${log.source}] [${log.type}] ${log.message}${log.details ? '\n' + log.details : ''}`
    ).join('\n');

    try {
      await navigator.clipboard.writeText(text);
      alert('Copied to clipboard!');
    } catch (err) {
      // Fallback for non-HTTPS or older browsers
      const textArea = document.createElement('textarea');
      textArea.value = text;
      textArea.style.position = 'fixed';
      textArea.style.left = '-9999px';
      document.body.appendChild(textArea);
      textArea.select();
      try {
        document.execCommand('copy');
        alert('Copied to clipboard!');
      } catch (e) {
        // Show the text in a prompt so user can manually copy
        prompt('Copy this text:', text);
      }
      document.body.removeChild(textArea);
    }
  };

  return (
    <>
      {/* Floating Tab Button - Always visible, super high z-index */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`fixed right-0 top-1/2 -translate-y-1/2 z-[9999] transition-all duration-200 ${
          isOpen ? 'translate-x-[400px]' : ''
        }`}
        title="Toggle Debug Panel"
      >
        <div className={`flex items-center ${
          hasErrors
            ? 'bg-red-600 hover:bg-red-700'
            : 'bg-slate-700 hover:bg-slate-600'
        } rounded-l-lg shadow-lg border border-r-0 border-slate-600 transition-colors`}>
          <div className="px-1 py-3 flex flex-col items-center gap-1">
            <svg
              className={`w-4 h-4 text-white transition-transform ${isOpen ? 'rotate-180' : ''}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            <span className="text-white text-[10px] font-bold writing-mode-vertical" style={{ writingMode: 'vertical-rl', textOrientation: 'mixed' }}>
              DEBUG
            </span>
            {logs.length > 0 && (
              <span className="text-[9px] text-slate-300 bg-slate-800 px-1 rounded">
                {logs.length}
              </span>
            )}
            {hasErrors && (
              <span className="w-2 h-2 bg-red-400 rounded-full animate-pulse" />
            )}
          </div>
        </div>
      </button>

      {/* Debug Panel - Slides out from right */}
      <div
        className={`fixed right-0 top-0 h-full w-[400px] bg-slate-900 border-l border-slate-700 shadow-xl z-[9998] flex flex-col transition-transform duration-200 ${
          isOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
        onContextMenu={(e) => e.stopPropagation()} // Prevent right-click from closing
      >
        {/* Header */}
        <div className="flex items-center justify-between p-2 border-b border-slate-700 bg-slate-800">
          <div className="flex items-center gap-2">
            <span className="text-sm">🔧</span>
            <h2 className="text-white font-medium text-sm">Debug Log</h2>
            <span className="text-[10px] text-slate-400">({logs.length})</span>
          </div>
          <div className="flex items-center gap-1">
            <label className="flex items-center gap-1 text-[10px] text-slate-400 cursor-pointer">
              <input
                type="checkbox"
                checked={autoScroll}
                onChange={(e) => setAutoScroll(e.target.checked)}
                className="w-3 h-3"
              />
              Auto
            </label>
            <button
              onClick={clearDebugLogs}
              className="px-1.5 py-0.5 text-[10px] bg-slate-700 hover:bg-slate-600 text-slate-300 rounded"
            >
              Clear
            </button>
            <button
              onClick={copyToClipboard}
              className="px-1.5 py-0.5 text-[10px] bg-blue-600 hover:bg-blue-700 text-white rounded"
              title="Copy all logs to clipboard"
            >
              Copy
            </button>
            <button
              onClick={() => setIsOpen(false)}
              className="ml-1 text-slate-400 hover:text-white"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Logs */}
        <div
          className="flex-1 overflow-y-auto p-2 font-mono text-[11px] select-text"
          onContextMenu={(e) => e.stopPropagation()} // Allow right-click for copy
        >
          {logs.length === 0 ? (
            <div className="text-slate-500 text-center py-8 text-xs">
              No logs yet. Create a ticket or time entry to see debug output.
            </div>
          ) : (
            logs.map((log, i) => (
              <div key={i} className="mb-1.5 pb-1.5 border-b border-slate-800/50">
                <div className="flex items-start gap-1.5 flex-wrap">
                  <span className="text-slate-500 whitespace-nowrap text-[10px]">{formatTime(log.timestamp)}</span>
                  <span className={`px-1 py-0.5 rounded text-[9px] text-white ${getSourceColor(log.source)}`}>
                    {log.source}
                  </span>
                  <span className={`flex-1 ${getTypeColor(log.type)}`}>
                    {log.message}
                  </span>
                </div>
                {log.details && (
                  <pre className="mt-1 text-[10px] text-slate-400 whitespace-pre-wrap break-all bg-slate-800/50 rounded p-1 select-text overflow-x-auto">
                    {log.details}
                  </pre>
                )}
              </div>
            ))
          )}
          <div ref={logsEndRef} />
        </div>
      </div>
    </>
  );
}
